import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Globe, Search, ShoppingCart, Clock, CheckCircle, XCircle,
  RefreshCw, ChevronDown, ChevronUp, Wallet, AlertTriangle, AlertCircle,
  Mail, Eye, X, Shield, Lock, Tag,
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { DomainRequestModal } from '@/components/DomainRequestModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const DEFAULT_EMAIL_SETTINGS = { email_price_per_account: 360, email_original_price: 900, email_hosting_features: [] };

export default function BuyDomain() {
  const { user } = useAuth();

  const [activeTab, setActiveTab]     = useState('search');
  const [catalog, setCatalog]         = useState([]);
  const [orders, setOrders]           = useState([]);
  const [domainRequests, setDomainRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [renewingOrder, setRenewingOrder]   = useState(null);
  const [renewYears, setRenewYears]         = useState(1);
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [viewingOrder, setViewingOrder]     = useState(null);

  // Email pricing for client
  const [emailSettings, setEmailSettings] = useState(DEFAULT_EMAIL_SETTINGS);

  // Search
  const [searchLabel, setSearchLabel]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]         = useState(false);
  const [years, setYears]                 = useState(1);
  const [buyingDomain, setBuyingDomain]   = useState('');
  const [showCatalog, setShowCatalog]     = useState(true);
  const [catalogFilter, setCatalogFilter] = useState('');

  // TLD row expansion for multi-year pricing
  const [expandedTld, setExpandedTld] = useState(null);

  // Orders
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expiryAlerts, setExpiryAlerts]   = useState([]);
  const [showAlert, setShowAlert]         = useState(true);

  // Domain Request Modal
  const [modalOpen, setModalOpen]         = useState(false);
  const [pendingDomain, setPendingDomain] = useState('');
  const [pendingPrice, setPendingPrice]   = useState(0);
  const [modalIsEmailOnly, setModalIsEmailOnly] = useState(false);
  const [modalSubmitting, setModalSubmitting]   = useState(false);

  // ── Fetchers ─────────────────────────────────────────────────────────────────
  const fetchCatalog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/domain-pricing`);
      setCatalog(res.data || []);
    } catch { /* silent */ }
  }, []);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await axios.get(`${API}/client/domain-orders`);
      const all = res.data?.orders || [];
      setOrders(all);
      // Compute expiry alerts (domains expiring within 30 days)
      const now = Date.now();
      const threshold = now + 30 * 24 * 60 * 60 * 1000;
      const alerts = [];
      const seen = new Set();
      all.filter(o => !o.type || o.type !== 'renewal').forEach(o => {
        if (seen.has(o.domain)) return;
        seen.add(o.domain);
        let expiry = o.expires_at ? new Date(o.expires_at).getTime() : null;
        if (!expiry && o.created_at) {
          expiry = new Date(o.created_at).getTime() + (o.years || 1) * 365 * 86400000;
        }
        if (expiry && expiry <= threshold) {
          const daysLeft = Math.round((expiry - now) / 86400000);
          alerts.push({ domain: o.domain, daysLeft, expiry: new Date(expiry).toISOString() });
        }
      });
      setExpiryAlerts(alerts);
    } catch { toast.error('Failed to load domain orders'); }
    finally { setOrdersLoading(false); }
  }, []);

  const fetchWallet = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wallet/balance`);
      setWalletBalance(res.data?.balance || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchCatalog();
    fetchWallet();
    // Fetch effective email pricing for client
    axios.get(`${API}/domain-settings/effective`)
      .then(r => setEmailSettings(r.data))
      .catch(() => axios.get(`${API}/domain-settings/public`)
        .then(r => setEmailSettings(r.data))
        .catch(() => {}));
  }, [fetchCatalog, fetchWallet]);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
    if (activeTab === 'requests') fetchRequests();
  }, [activeTab, fetchOrders]);

  // Recalculate search result prices when years or catalog changes
  useEffect(() => {
    if (!searchResults.length || !catalog.length) return;
    setSearchResults(prev => prev.map(d => {
      const tldKey   = d.domainName.split('.').slice(1).join('.');
      const tldEntry = catalog.find(c => c.tld === tldKey);
      const yrRow    = tldEntry?.yearly_pricing?.find(y => y.year === years);
      const price    = yrRow?.price ?? tldEntry?.price?.add ?? d.price;
      return { ...d, clientPrice: price };
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years, catalog]);

  const fetchRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const res = await axios.get(`${API}/domain/requests`);
      setDomainRequests(res.data || []);
    } catch { /* silent */ }
    finally { setRequestsLoading(false); }
  }, []);

  // ── Domain Search ─────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    const label = searchLabel.trim().toLowerCase().split('.')[0];
    if (!label) { toast.error('Please enter a domain name (e.g. mycompany)'); return; }
    setSearching(true);
    setSearchResults([]);
    try {
      const res = await axios.get(`${API}/white-label/domain/search`, { params: { name: label } });
      // Overlay client-specific prices from catalog
      const domains = (res.data?.domains || []).map(d => {
        const tldKey   = d.domainName.split('.').slice(1).join('.');
        const tldEntry = catalog.find(c => c.tld === tldKey);
        const yrRow    = tldEntry?.yearly_pricing?.find(y => y.year === years);
        const price    = yrRow?.price ?? tldEntry?.price?.add ?? d.price;
        return { ...d, clientPrice: price };
      });
      setSearchResults(domains);
      if (!domains.length) toast.info('No results. Try a different name.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Domain search failed. Works on live production server.');
    } finally {
      setSearching(false);
    }
  };

  const handleRequest = (domain) => {
    // Find price from search results
    const result = searchResults.find(r => r.domainName === domain);
    const price  = result?.clientPrice ?? 0;
    setPendingDomain(domain);
    setPendingPrice(price);
    setModalIsEmailOnly(false);
    setModalOpen(true);
  };

  const handleEmailOnlyRequest = (domain) => {
    setPendingDomain(domain);
    setPendingPrice(0);
    setModalIsEmailOnly(true);
    setModalOpen(true);
  };

  const handleRenewSubmit = async () => {
    if (!renewingOrder) return;
    setRenewSubmitting(true);
    try {
      await axios.post(`${API}/domain/renew-request`, {
        domain_order_id: renewingOrder.id,
        years: renewYears,
      });
      toast.success(`Renewal request for ${renewingOrder.domain} (${renewYears}yr) submitted! Your partner will review it.`);
      setRenewingOrder(null);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Renewal request failed');
    } finally {
      setRenewSubmitting(false);
    }
  };

  const handleModalSubmit = async (payload) => {
    setModalSubmitting(true);
    try {
      const res = await axios.post(`${API}/domain/request`, payload);
      toast.success(res.data.message || `Request submitted for ${payload.domain}!`);
      setModalOpen(false);
      if (!payload.email_only) {
        setSearchResults(prev => prev.filter(d => d.domainName !== payload.domain));
        setActiveTab('requests');
      }
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request submission failed');
    } finally {
      setModalSubmitting(false);
    }
  };

  const filteredCatalog = catalog.filter(t =>
    !catalogFilter || t.tld.toLowerCase().includes(catalogFilter.toLowerCase())
  );
  const popularTlds = filteredCatalog.filter(t => t.popular);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Globe className="w-6 h-6 text-blue-600" />
              Buy a Domain
            </h1>
            <p className="text-slate-500 mt-1 text-sm">Search and register your domain name</p>
          </div>
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
            <Wallet className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-700 font-medium">Wallet: {fmt(walletBalance)}</span>
          </div>
        </div>

        {/* Expiry alert (shown after loading orders) */}
        {expiryAlerts.length > 0 && showAlert && (
          <div data-testid="expiry-alert-banner"
            className={`rounded-xl border px-4 py-3 flex flex-col gap-2
              ${expiryAlerts.some(a => a.daysLeft < 0) ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {expiryAlerts.some(a => a.daysLeft <= 7)
                  ? <AlertCircle className="w-4 h-4 text-red-500" />
                  : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                <span className="text-sm font-semibold text-slate-800">
                  {expiryAlerts.length} domain{expiryAlerts.length > 1 ? 's' : ''} expiring soon
                </span>
              </div>
              <button onClick={() => setShowAlert(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {expiryAlerts.map(a => (
                <span key={a.domain}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium
                    ${a.daysLeft < 0 ? 'bg-red-100 text-red-700 border border-red-200'
                    : a.daysLeft <= 7 ? 'bg-orange-100 text-orange-700 border border-orange-200'
                    : 'bg-amber-100 text-amber-700 border border-amber-200'}`}>
                  <span className="font-mono">{a.domain}</span>
                  {' · '}
                  {a.daysLeft < 0 ? 'Expired' : a.daysLeft === 0 ? 'Expires today' : `${a.daysLeft}d left`}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { key: 'search', label: 'Search & Register', icon: Search },
              { key: 'pricing', label: `Domain Pricing (${catalog.filter(t => t.tld).length} TLDs)`, icon: Tag },
              { key: 'requests', label: 'My Domain Orders', icon: Clock },
              { key: 'orders', label: 'Registered Domains', icon: CheckCircle },
            ].map(({ key, label, icon: Icon }) => (
              <button key={key} data-testid={`tab-${key}`}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                  ${activeTab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                <Icon className="w-4 h-4" />
                {label}
                {key === 'requests' && domainRequests.length > 0 && (
                  <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">{domainRequests.length}</span>
                )}
                {key === 'orders' && orders.length > 0 && (
                  <span className="bg-slate-100 text-slate-600 text-xs px-1.5 py-0.5 rounded-full">{orders.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ══ SEARCH TAB ══ */}
        {activeTab === 'search' && (
          <div className="space-y-5">

            {/* Search box */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-700 mb-4">Check Domain Availability</h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  data-testid="domain-search-input"
                  placeholder="Enter domain name (e.g. mycompany)"
                  value={searchLabel}
                  onChange={e => setSearchLabel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="flex-1"
                />
                <select
                  data-testid="years-select"
                  value={years}
                  onChange={e => setYears(parseInt(e.target.value))}
                  className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white h-10 sm:w-36"
                >
                  {[1,2,3,4,5,6,7,8,9,10].map(y => (
                    <option key={y} value={y}>{y} Year{y > 1 ? 's' : ''}</option>
                  ))}
                </select>
                <Button data-testid="search-btn" onClick={handleSearch} disabled={searching}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2 whitespace-nowrap">
                  {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {searching ? 'Checking…' : 'Check Availability'}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-3">
                See all available TLDs and pricing in the <button className="text-blue-600 underline" onClick={() => setActiveTab('pricing')}>Domain Pricing tab</button>.
              </p>
            </div>

            {searchResults.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                  <span className="text-sm font-semibold text-slate-700">Results for "{searchLabel.trim()}"</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {searchResults.map(d => (
                    <div key={d.domainName} data-testid={`result-${d.domainName}`}
                      className="flex items-center justify-between px-5 py-4 hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        {d.available
                          ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
                        <div>
                          <span className="font-medium text-slate-800">{d.domainName}</span>
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium
                            ${d.available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>
                            {d.available ? 'Available' : 'Taken'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm font-semibold text-slate-800">{fmt(d.clientPrice ?? d.price)}</div>
                          <div className="text-xs text-slate-400">{years} year{years > 1 ? 's' : ''}</div>
                        </div>
                        {d.available && (
                          <Button size="sm" data-testid={`buy-btn-${d.domainName}`}
                            onClick={() => handleRequest(d.domainName)}
                            className="bg-blue-600 hover:bg-blue-700 text-white gap-1">
                            <ShoppingCart className="w-3.5 h-3.5" />
                            Request
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ DOMAIN PRICING TAB ══ */}
        {activeTab === 'pricing' && (
          <div className="space-y-5">

            {/* Email Hosting Pricing Card */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5" data-testid="client-email-pricing-card">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-sm font-semibold text-slate-800">Email Hosting</span>
                    <span className="text-xs line-through text-slate-400">{fmt(emailSettings.email_original_price)}/acc/yr</span>
                    <span className="text-sm font-bold text-green-700">{fmt(emailSettings.email_price_per_account)}/account/year</span>
                    {emailSettings.email_original_price > 0 && emailSettings.email_price_per_account < emailSettings.email_original_price && (
                      <span className="text-[10px] font-bold bg-green-600 text-white px-1.5 py-0.5 rounded-full">
                        SAVE {Math.round((1 - emailSettings.email_price_per_account / emailSettings.email_original_price) * 100)}%
                      </span>
                    )}
                  </div>
                  {emailSettings.email_hosting_features?.length > 0 && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {emailSettings.email_hosting_features.map((f, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600">
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* WHOIS + Domain Protector+ Pricing */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
                <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">WHOIS Privacy</p>
                  <p className="text-base font-bold text-blue-700">{fmt(emailSettings.whois_privacy_price)}<span className="text-xs text-slate-400 font-normal">/year</span></p>
                  <p className="text-xs text-slate-400">Hides personal contact info from public WHOIS database</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Domain Protector+</p>
                  <p className="text-base font-bold text-blue-700">{fmt(emailSettings.domain_protector_price || 199)}<span className="text-xs text-slate-400 font-normal">/year</span></p>
                  <p className="text-xs text-slate-400">Prevents unauthorized transfers and domain hijacking</p>
                </div>
              </div>
            </div>

            {/* TLD Catalog (moved from Search tab) */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
                <button onClick={() => setShowCatalog(p => !p)}
                  className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900">
                  All TLD Pricing ({filteredCatalog.length} TLDs)
                  {showCatalog ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <Button size="sm" variant="outline" className="text-xs gap-1 h-8"
                  onClick={() => { setActiveTab('search'); }}>
                  <Search className="w-3 h-3" /> Search & Register
                </Button>
              </div>

              {showCatalog && (
                <>
                  <div className="px-5 py-2 border-b border-slate-100">
                    <Input placeholder="Filter TLDs (e.g. com, in)"
                      value={catalogFilter} onChange={e => setCatalogFilter(e.target.value)}
                      className="max-w-xs h-8 text-sm" />
                  </div>

                  {/* Popular grid */}
                  {popularTlds.length > 0 && (
                    <div className="px-5 py-4 border-b border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Popular Domains</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                        {popularTlds.map(t => (
                          <div key={t.tld} data-testid={`catalog-tld-${t.tld}`}
                            className="border border-slate-200 rounded-xl p-3 text-center hover:border-blue-300 hover:bg-blue-50/30 transition-colors cursor-pointer"
                            onClick={() => { setActiveTab('search'); setSearchLabel(t.tld); }}>
                            <p className="font-mono font-bold text-slate-800 text-sm">.{t.tld}</p>
                            <p className="text-blue-700 font-semibold text-xs mt-1">{fmt(t.price?.add || 0)}</p>
                            <p className="text-slate-400 text-[10px]">/year</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Table */}
                  <div>
                    <div className="grid text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 px-5 py-2.5 border-b border-slate-100"
                      style={{ gridTemplateColumns: '1fr 140px 140px 40px' }}>
                      <span>TLD</span>
                      <span className="text-right">Register (1yr)</span>
                      <span className="text-right">Renew (1yr)</span>
                      <span />
                    </div>
                    {filteredCatalog.map(t => {
                      const isExpanded = expandedTld === t.tld;
                      const yearRows = Array.from({ length: 10 }, (_, i) => {
                        const yr = i + 1;
                        const yp = t.yearly_pricing?.find(y => y.year === yr);
                        const regPrice = yp?.price ?? (t.price?.add || 0) * yr;
                        const renewPrice = yp?.renew_price ?? (t.price?.renew || 0) * yr;
                        return { yr, regPrice, renewPrice };
                      }).filter(r => r.regPrice > 0);
                      return (
                        <div key={t.tld} data-testid={`tld-row-${t.tld}`}>
                          <div className="grid items-center px-5 py-3 border-b border-slate-50 hover:bg-slate-50 text-sm cursor-pointer"
                            style={{ gridTemplateColumns: '1fr 140px 140px 40px' }}
                            onClick={() => setExpandedTld(isExpanded ? null : t.tld)}>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-slate-800">.{t.tld}</span>
                              {t.popular && <span className="text-[9px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">HOT</span>}
                            </div>
                            <div className="text-right font-semibold text-blue-700">{fmt(t.price?.add || 0)}</div>
                            <div className="text-right text-slate-500">{fmt(t.price?.renew || 0)}</div>
                            <div className="flex justify-center">
                              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="border-b border-slate-100 bg-blue-50/40 px-5 py-3">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Multi-Year Pricing — .{t.tld}</p>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs min-w-[400px]">
                                  <thead>
                                    <tr className="text-slate-500 border-b border-blue-100">
                                      <th className="text-left py-1.5 font-semibold w-16">Years</th>
                                      <th className="text-right py-1.5 font-semibold">Register</th>
                                      <th className="text-right py-1.5 font-semibold">Renew</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {yearRows.map(({ yr, regPrice, renewPrice }) => (
                                      <tr key={yr} className="border-b border-blue-100/50 last:border-0 hover:bg-blue-50">
                                        <td className="py-1.5 font-medium text-slate-700">{yr} {yr === 1 ? 'Year' : 'Years'}</td>
                                        <td className="py-1.5 text-right font-semibold text-blue-700">{fmt(regPrice)}</td>
                                        <td className="py-1.5 text-right text-slate-500">{fmt(renewPrice)}</td>
                                      </tr>
                                    ))}
                                    {yearRows.length === 0 && (
                                      <tr><td colSpan={3} className="py-2 text-slate-400 text-center">No pricing data available</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {filteredCatalog.length === 0 && (
                      <div className="py-8 text-center text-slate-400 text-sm">No TLDs match your filter</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ MY DOMAIN ORDERS TAB ══ */}
        {activeTab === 'requests' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-sm font-semibold text-slate-700">My Domain Orders</h2>
              <Button variant="outline" size="sm" onClick={fetchRequests} disabled={requestsLoading} className="gap-1 text-xs">
                <RefreshCw className={`w-3.5 h-3.5 ${requestsLoading ? 'animate-spin' : ''}`} />Refresh
              </Button>
            </div>

            {/* Order Details Modal */}
            {viewingOrder && (() => {
              const r = viewingOrder;
              const isQuoted    = (r.quoted_total || 0) > 0;
              const domainBase  = isQuoted ? (r.quoted_domain_price || 0) : Math.max(0, (r.price || 0) - (r.whois_addon_price || 0));
              const whoisCost   = isQuoted ? (r.quoted_whois_cost || 0) : (r.whois_addon_price || 0);
              const emailCost   = isQuoted ? (r.quoted_email_cost || 0) : (r.email_cost_estimate || 0);
              const dpCost      = isQuoted ? (r.quoted_protector_cost || 0) : (r.domain_protector_cost || 0);
              const wholesaleTotal = (r.price || 0) + (r.email_cost_estimate || 0) + (r.domain_protector_cost || 0);
              const displayTotal   = isQuoted ? r.quoted_total : r.client_facing_total > 0 ? r.client_facing_total : wholesaleTotal;
              const statusMap = {
                pending_sp: 'Pending Approval', pending_cp: 'Pending Approval',
                pending_admin: 'Pending Admin Approval', payment_pending: 'Payment Pending',
                registered: 'Registered', rejected: 'Rejected',
              };
              return (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="order-details-modal">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                      <div>
                        <h2 className="text-base font-semibold text-slate-800 font-mono">{r.domain}</h2>
                        <p className="text-xs text-slate-400 mt-0.5">Order Breakdown</p>
                      </div>
                      <button onClick={() => setViewingOrder(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" data-testid="close-order-details">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="px-6 py-5 space-y-4">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${r.status === 'registered' ? 'bg-green-100 text-green-700' : r.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                          {statusMap[r.status] || r.status}
                        </span>
                        <span className="text-xs text-slate-400">{r.years} year{r.years !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-100">
                        {!r.email_only && (
                          <div className="flex justify-between px-4 py-2.5 text-sm">
                            <span className="text-slate-600">Domain ({r.years}yr)</span>
                            <span className="font-semibold text-slate-800">{fmt(domainBase)}</span>
                          </div>
                        )}
                        {whoisCost > 0 && (
                          <div className="flex justify-between px-4 py-2.5 text-sm">
                            <span className="text-slate-600 flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-slate-400" /> WHOIS Privacy</span>
                            <span className="font-semibold text-slate-800">{fmt(whoisCost)}</span>
                          </div>
                        )}
                        {emailCost > 0 && (
                          <div className="flex justify-between px-4 py-2.5 text-sm">
                            <span className="text-slate-600 flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-violet-500" /> Email ({r.email_accounts_count} acc × {r.years}yr)</span>
                            <span className="font-semibold text-violet-700">{fmt(emailCost)}</span>
                          </div>
                        )}
                        {dpCost > 0 && (
                          <div className="flex justify-between px-4 py-2.5 text-sm">
                            <span className="text-slate-600 flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-blue-600" /> Domain Protector+</span>
                            <span className="font-semibold text-blue-700">{fmt(dpCost)}</span>
                          </div>
                        )}
                        {(r.ssl_required || !r.email_only) && (
                          <div className="flex justify-between px-4 py-2.5 text-sm">
                            <span className="text-slate-500">SSL Certificate</span>
                            <span className="font-semibold text-green-600">Free</span>
                          </div>
                        )}
                        {isQuoted && (
                          <>
                            <div className="flex justify-between px-4 py-2.5 text-sm">
                              <span className="text-slate-600">Subtotal (excl. GST)</span>
                              <span className="font-semibold text-slate-800">{fmt(r.quoted_subtotal || 0)}</span>
                            </div>
                            <div className="flex justify-between px-4 py-2.5 text-sm">
                              <span className="text-slate-600">GST (18% IGST)</span>
                              <span className="font-semibold text-slate-800">{fmt(r.quoted_gst || 0)}</span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between px-4 py-3 bg-blue-600 rounded-b-xl">
                          <span className="text-sm font-bold text-blue-100">{isQuoted ? 'Total (incl. GST)' : 'Total'}</span>
                          <span className="text-sm font-extrabold text-white">{fmt(displayTotal)}</span>
                        </div>
                      </div>
                      {r.status === 'rejected' && r.rejection_reason && (
                        <div className="bg-red-50 rounded-xl border border-red-100 px-4 py-3 text-sm text-red-700">{r.rejection_reason}</div>
                      )}
                      <p className="text-xs text-slate-400 text-right">Submitted: {r.created_at ? new Date(r.created_at).toLocaleString('en-IN') : '—'}</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {requestsLoading ? (
              <div className="flex justify-center py-14"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
            ) : domainRequests.length === 0 ? (
              <div className="text-center py-14">
                <Globe className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 text-sm font-medium">No domain requests found</p>
                <p className="text-slate-400 text-xs mt-1">Go to the Search tab to request a domain.</p>
                <Button size="sm" className="mt-4 bg-blue-600 hover:bg-blue-700 text-white gap-1"
                  onClick={() => setActiveTab('search')}>
                  <Search className="w-3.5 h-3.5" /> Search for a Domain
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      {['Domain', 'Years', 'Total Price', 'Requested On', 'Status', ''].map(h => (
                        <th key={h} className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {domainRequests.map((r) => {
                      const statusMap = {
                        pending_sp:    { label: 'Pending Approval',    cls: 'bg-amber-100 text-amber-700' },
                        pending_cp:    { label: 'Pending Approval',    cls: 'bg-amber-100 text-amber-700' },
                        pending_admin: { label: 'Pending Approval', cls: 'bg-blue-100 text-blue-700' },
                        payment_pending: { label: 'Payment Pending',      cls: 'bg-red-100 text-red-600' },
                        registered:    { label: 'Registered',             cls: 'bg-green-100 text-green-700' },
                        rejected:      { label: 'Rejected',               cls: 'bg-red-100 text-red-600' },
                      };
                      const s = statusMap[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-600' };
                      const isQuoted = (r.quoted_total || 0) > 0;
                      const displayTotal = isQuoted
                        ? r.quoted_total
                        : r.client_facing_total > 0
                          ? r.client_facing_total
                          : (r.price || 0) + (r.email_cost_estimate || 0) + (r.domain_protector_cost || 0);
                      const isClientPrice = isQuoted || (r.client_facing_total || 0) > 0;
                      return (
                        <tr key={r.id} className="hover:bg-slate-50" data-testid={`req-row-${r.id}`}>
                          <td className="px-5 py-3 font-mono font-semibold text-slate-800">
                            {r.domain}
                            {r.renewal && <span className="ml-2 text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">RENEWAL</span>}
                          </td>
                          <td className="px-5 py-3 text-slate-600">{r.years} yr</td>
                          <td className="px-5 py-3 text-blue-700 font-semibold">
                            <span className="flex flex-col">
                              <span>{r.email_only ? '—' : fmt(displayTotal)}</span>
                              {isQuoted && (
                                <span className="text-[10px] text-slate-400 font-normal">incl. addons + GST</span>
                              )}
                              {!isClientPrice && ((r.email_cost_estimate || 0) + (r.domain_protector_cost || 0)) > 0 && (
                                <span className="text-[10px] text-slate-400 font-normal">incl. addons</span>
                              )}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(r.created_at)}</td>
                          <td className="px-5 py-3">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${s.cls}`}>{s.label}</span>
                            {r.status === 'rejected' && r.rejection_reason && (
                              <p className="text-xs text-red-400 mt-0.5 max-w-[180px]">{r.rejection_reason}</p>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <button
                              data-testid={`view-order-${r.id}`}
                              onClick={() => setViewingOrder(r)}
                              className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-colors"
                              title="View order breakdown"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══ REGISTERED DOMAINS TAB ══ */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-sm font-semibold text-slate-700">Registered Domains</h2>
              <Button variant="outline" size="sm" onClick={fetchOrders} disabled={ordersLoading} className="gap-1 text-xs">
                <RefreshCw className={`w-3.5 h-3.5 ${ordersLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {ordersLoading ? (
              <div className="flex justify-center py-14"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
            ) : orders.length === 0 ? (
              <div className="text-center py-14">
                <Globe className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 text-sm font-medium">No domains found</p>
                <p className="text-slate-400 text-xs mt-1">
                  Search for a domain above to get started.
                </p>
                <Button size="sm" className="mt-4 bg-blue-600 hover:bg-blue-700 text-white gap-1"
                  onClick={() => setActiveTab('search')}>
                  <Search className="w-3.5 h-3.5" /> Search for a Domain
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      {['#', 'Domain', 'Years', 'Registered', 'Expires', 'Status', 'Actions'].map(h => (
                        <th key={h} className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.map((o, i) => {
                      let expiryStr = o.expires_at;
                      if (!expiryStr && o.created_at) {
                        const d = new Date(o.created_at);
                        d.setDate(d.getDate() + (o.years || 1) * 365);
                        expiryStr = d.toISOString();
                      }
                      const daysLeft = expiryStr ? Math.round((new Date(expiryStr) - Date.now()) / 86400000) : null;
                      const isExpiring = daysLeft !== null && daysLeft <= 30;
                      const isExpired  = daysLeft !== null && daysLeft < 0;

                      return (
                        <tr key={o.id || i} data-testid={`order-row-${i}`}
                          className={`hover:bg-slate-50 transition-colors ${isExpired ? 'bg-red-50/30' : isExpiring ? 'bg-amber-50/30' : ''}`}>
                          <td className="px-5 py-3 text-slate-400 text-xs">{i + 1}</td>
                          <td className="px-5 py-3">
                            <span className="font-medium text-slate-800 font-mono">{o.domain}</span>
                            {o.email_hosting_requested && o.years > 0 && (
                              <div className="mt-1">
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full">
                                  <Mail className="w-2.5 h-2.5" />
                                  {o.years}yr Email Included
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3 text-slate-600 text-xs">{o.years} yr</td>
                          <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(o.created_at)}</td>
                          <td className="px-5 py-3 text-xs">
                            {expiryStr ? (
                              <div>
                                <span className={`font-medium ${isExpired ? 'text-red-600' : isExpiring ? 'text-amber-600' : 'text-slate-600'}`}>
                                  {fmtDate(expiryStr)}
                                </span>
                                {daysLeft !== null && (
                                  <p className={`text-[10px] mt-0.5 ${isExpired ? 'text-red-400' : isExpiring ? 'text-amber-500' : 'text-slate-400'}`}>
                                    {isExpired ? `Expired ${Math.abs(daysLeft)}d ago` : daysLeft === 0 ? 'Expires today' : `${daysLeft}d left`}
                                  </p>
                                )}
                              </div>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-5 py-3">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium
                              ${isExpired ? 'bg-red-100 text-red-700'
                              : isExpiring ? 'bg-amber-100 text-amber-700'
                              : 'bg-green-100 text-green-700'}`}>
                              {isExpired ? 'Expired' : isExpiring ? 'Expiring Soon' : o.status || 'Active'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button
                                size="sm"
                                data-testid={`renew-btn-${o.id || i}`}
                                onClick={() => { setRenewingOrder(o); setRenewYears(1); }}
                                className="text-xs gap-1 bg-green-600 hover:bg-green-700 text-white h-7"
                              >
                                <RefreshCw className="w-3 h-3" />
                                Renew
                              </Button>
                              <Button
                                size="sm" variant="outline"
                                data-testid={`email-hosting-btn-${i}`}
                                onClick={() => handleEmailOnlyRequest(o.domain)}
                                className="text-xs gap-1 text-violet-600 border-violet-200 hover:bg-violet-50 h-7"
                              >
                                <Mail className="w-3 h-3" />
                                + Email
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Renewal Modal */}
      {renewingOrder && (() => {
        const renewPrice = (() => {
          const tldData = catalog.find(t => t.tld === renewingOrder.tld);
          if (tldData) {
            const yr = (tldData.yearly_pricing || []).find(p => p.year === renewYears);
            return yr?.price ?? (tldData.price?.renew || tldData.price?.add || 0) * renewYears;
          }
          return 0;
        })();
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="renewal-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">Renew Domain</h2>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">{renewingOrder.domain}</p>
                </div>
                <button onClick={() => setRenewingOrder(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-5">
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-3">Renewal Duration</label>
                  <div className="grid grid-cols-5 gap-2">
                    {[1, 2, 3, 5, 10].map(y => (
                      <button key={y} data-testid={`renew-years-${y}`}
                        onClick={() => setRenewYears(y)}
                        className={`py-2 rounded-xl border text-sm font-semibold transition-all ${renewYears === y ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-200 text-slate-700 hover:border-blue-300'}`}>
                        {y}yr
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Domain Renewal ({renewYears}yr)</span>
                    <span className="font-semibold text-slate-800">{renewPrice > 0 ? fmt(renewPrice) : '—'}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 flex justify-between">
                    <span className="text-sm font-bold text-slate-700">Estimated Total</span>
                    <span className="text-sm font-bold text-blue-700">{renewPrice > 0 ? fmt(renewPrice) : 'Check with partner'}</span>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
                  Renewal request goes to your partner for approval. You'll be notified once processed.
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setRenewingOrder(null)} className="flex-1">Cancel</Button>
                  <Button
                    data-testid="confirm-renew-btn"
                    onClick={handleRenewSubmit}
                    disabled={renewSubmitting}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-2">
                    {renewSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {renewSubmitting ? 'Submitting…' : `Submit Renewal (${renewYears}yr)`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Domain Request Modal */}
      <DomainRequestModal
        open={modalOpen}
        onClose={() => !modalSubmitting && setModalOpen(false)}
        domain={pendingDomain}
        years={years}
        price={pendingPrice}
        isEmailOnly={modalIsEmailOnly}
        onSubmit={handleModalSubmit}
        submitting={modalSubmitting}
      />
    </DashboardLayout>
  );
}
